import { CheckCircle2, FileText, ListChecks, Users, Building2, Search, ShieldAlert, ChevronRight } from "lucide-react";

/**
 * Tutorial de Registro Legal para el rol Calidad.
 * Versión React/JSX del HTML original /opt/SICEN/Insumos/Tutoriales/Tutorial Registro Legal Calidad.html.
 * Reescrito con Tailwind para mantener experiencia visual consistente con el resto de la app.
 */
export function TutorialCalidad() {
  return (
    <div className="space-y-5">
      <header className="rounded-lg border bg-surface p-6 shadow-sm">
        <div className="text-xs font-bold uppercase tracking-wider text-primary-strong">SICEN · Tutorial</div>
        <h1 className="mt-2 text-2xl font-semibold text-fg">Registro Legal — rol Calidad</h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-fg-muted">
          Esta guía está orientada al personal de Calidad responsable de capturar, completar y consultar
          expedientes de Registro Legal dentro del sistema SICEN.
        </p>
      </header>

      <div className="grid gap-5 lg:grid-cols-[260px_1fr]">
        <aside className="self-start rounded-lg border bg-surface p-4 shadow-sm lg:sticky lg:top-4">
          <h3 className="mb-3 text-sm font-semibold text-fg">Contenido</h3>
          <ul className="space-y-1 text-sm">
            <li><a href="#objetivo" className="block rounded px-2 py-1 text-fg-muted hover:bg-surface-2 hover:text-primary-strong">1. Objetivo</a></li>
            <li><a href="#acceso" className="block rounded px-2 py-1 text-fg-muted hover:bg-surface-2 hover:text-primary-strong">2. Acceso al módulo</a></li>
            <li><a href="#flujo" className="block rounded px-2 py-1 text-fg-muted hover:bg-surface-2 hover:text-primary-strong">3. Flujo general</a></li>
            <li><a href="#pasos" className="block rounded px-2 py-1 text-fg-muted hover:bg-surface-2 hover:text-primary-strong">4. Paso a paso</a></li>
            <li><a href="#variantes" className="block rounded px-2 py-1 text-fg-muted hover:bg-surface-2 hover:text-primary-strong">5. Tipos de registro</a></li>
            <li><a href="#validaciones" className="block rounded px-2 py-1 text-fg-muted hover:bg-surface-2 hover:text-primary-strong">6. Validaciones y reglas</a></li>
            <li><a href="#consulta" className="block rounded px-2 py-1 text-fg-muted hover:bg-surface-2 hover:text-primary-strong">7. Consulta de registros</a></li>
            <li><a href="#recomendaciones" className="block rounded px-2 py-1 text-fg-muted hover:bg-surface-2 hover:text-primary-strong">8. Recomendaciones</a></li>
          </ul>
        </aside>

        <div className="space-y-6">
          <p className="text-sm leading-relaxed text-fg-muted">
            El módulo de Registro Legal es el punto de entrada del ciclo operativo dentro de SICEN. A través de
            este proceso se formaliza el expediente base de empresas, tramitadores y usuarios que posteriormente
            podrán operar solicitudes y dar seguimiento a sus trámites.
          </p>

          <div className="rounded-lg border-l-4 border-primary-strong bg-estado-info-bg p-4 text-sm leading-relaxed text-fg-muted">
            El alta de Registro Legal es una actividad reservada al rol Calidad. En esta liberación, los registros
            capturados por esta área quedan automáticamente en estado <strong>APROBADO</strong>. Además, cuando se
            crean usuarios asociados, el sistema genera una contraseña inicial automática para el primer ingreso.
          </div>

          <Section id="objetivo" icon={<CheckCircle2 className="h-5 w-5" />} title="Objetivo del tutorial">
            <p>
              Explicar cómo Calidad debe ingresar al módulo, seleccionar el tipo de registro, capturar la información
              principal del expediente, asociar usuarios y consultar posteriormente los registros creados.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <FactCard label="Rol objetivo" value="Calidad" />
              <FactCard label="Menú" value="Trámites Legales" />
              <FactCard label="Opciones visibles" value="Alta de Registro Legal y Consulta de Registros Legales" />
              <FactCard label="Resultado esperado" value="Expediente legal base correctamente capturado" />
            </div>
          </Section>

          <Section id="acceso" icon={<Search className="h-5 w-5" />} title="Acceso al módulo">
            <p>
              Ingrese al portal SICEN con su correo electrónico y contraseña. Una vez autenticado, el sistema mostrará
              las opciones disponibles con base en el rol asignado.
            </p>
            <ul className="ml-4 list-disc space-y-1">
              <li>Acceda al portal con sus credenciales.</li>
              <li>Ubique el menú <strong>Trámites Legales</strong> en la barra lateral.</li>
              <li>Seleccione <strong>Alta de Registro Legal</strong> para una captura nueva.</li>
              <li>Seleccione <strong>Consulta de Registros Legales</strong> para revisar expedientes existentes.</li>
            </ul>
          </Section>

          <Section id="flujo" icon={<ChevronRight className="h-5 w-5" />} title="Flujo general del proceso">
            <p>
              En la operación normal, Calidad recibe previamente el expediente, comparte formatos oficiales, valida la
              documentación y posteriormente realiza la captura del Registro Legal en SICEN.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <FactCard label="Entrada" value="Recepción del expediente y documentos" />
              <FactCard label="Captura" value="Alta de Registro Legal" />
              <FactCard label="Asociaciones" value="Empresa, usuarios, autorizados y tramitador" />
              <FactCard label="Salida" value="Registro base listo para operar en SICEN" />
            </div>
          </Section>

          <Section id="pasos" icon={<ListChecks className="h-5 w-5" />} title="Paso a paso para dar de alta un Registro Legal">
            <ol className="space-y-3">
              <Step n={1} title="Ingresar a Alta de Registro Legal">
                Desde el menú Trámites Legales, acceda a la opción de alta para iniciar la captura del expediente.
              </Step>
              <Step n={2} title="Seleccionar el tipo de registro">
                Defina si el expediente corresponde a tramitador externo, empresa titular o empresa titular con
                tramitador, y si se trata de persona moral o física.
              </Step>
              <Step n={3} title="Capturar datos generales">
                Registre nombre o razón social, RFC, domicilio fiscal, datos generales del solicitante y, cuando
                aplique, representantes legales.
              </Step>
              <Step n={4} title="Integrar documentación">
                Cargue o deje identificada la documentación correspondiente al expediente según el tipo de persona y
                el tipo de registro.
              </Step>
              <Step n={5} title="Registrar usuario solicitante">
                Capture o seleccione el usuario principal. Su correo será la cuenta de acceso al sistema cuando no
                exista previamente.
              </Step>
              <Step n={6} title="Agregar usuarios adicionales">
                Registre usuarios autorizados o gestores según el escenario. Cuando un usuario ya exista, debe
                asociarse y no duplicarse.
              </Step>
              <Step n={7} title="Validar información antes de guardar">
                Revise RFC, CURP, correo electrónico, archivos y reglas particulares del escenario seleccionado antes
                de concluir el alta.
              </Step>
              <Step n={8} title="Guardar el expediente">
                Concluya la captura. En esta liberación, los registros creados por Calidad quedan automáticamente
                aprobados.
              </Step>
            </ol>
          </Section>

          <Section id="variantes" icon={<Building2 className="h-5 w-5" />} title="Tipos de registro soportados">
            <div className="grid gap-3 sm:grid-cols-2">
              <Variant title="1. Tramitador externo — persona moral">
                Empresa que gestionará trámites para terceros. Requiere datos generales, RFC de persona moral,
                domicilio, representantes legales, usuario solicitante y usuarios gestores.
              </Variant>
              <Variant title="2. Tramitador externo — persona física">
                Similar al anterior pero como persona física. No aplica acta constitutiva y la documentación cambia
                conforme al esquema de persona física.
              </Variant>
              <Variant title="3. Empresa titular — persona moral">
                Empresa que solicitará servicios directamente. El usuario principal queda con rol{" "}
                <strong>TITULAR</strong>, y los usuarios adicionales con rol <strong>autorizado</strong>.
              </Variant>
              <Variant title="4. Empresa titular — persona física">
                Aplica para titulares persona física. Cambian la validación del RFC y la documentación, incluyendo
                CURP y documentos de identificación personal.
              </Variant>
              <Variant title="5. Empresa titular con tramitador — persona moral">
                Habilita el campo <strong>Empresa gestora</strong>, que solo muestra tramitadores ya registrados.
                Permite buscar y reutilizar usuarios asociados al tramitador.
              </Variant>
              <Variant title="6. Empresa titular con tramitador — persona física">
                Mantiene la lógica de empresa gestora y reutilización de usuarios, cambiando la validación del RFC y
                la documentación aplicable a persona física.
              </Variant>
            </div>
          </Section>

          <Section id="validaciones" icon={<ShieldAlert className="h-5 w-5" />} title="Validaciones y reglas importantes">
            <div className="grid gap-3 sm:grid-cols-2">
              <FactCard label="Correo" value="Debe ser válido y no duplicado si ya existe en el sistema" />
              <FactCard label="RFC" value="Se valida según persona moral o persona física" />
              <FactCard label="CURP" value="Se valida conforme a sus reglas de formato" />
              <FactCard label="Archivos" value="Tamaño máximo permitido por componente" />
              <FactCard label="Usuarios existentes" value="Deben asociarse, no duplicarse" />
              <FactCard label="Empresa gestora" value="Solo puede ser un tramitador previamente registrado" />
              <FactCard
                label="Contraseña inicial"
                value="Cuando se crea un usuario nuevo, el sistema genera una contraseña inicial para el primer ingreso"
                fullWidth
              />
            </div>
            <div className="rounded-lg border-l-4 border-estado-warn/25 bg-estado-warn-bg p-4 text-sm leading-relaxed text-fg-muted">
              Si un usuario ya existe en SICEN pero requiere una asociación especial que no corresponda al flujo
              normal del formulario, el caso debe escalarse a <strong>AdminSICEN</strong>.
            </div>
          </Section>

          <Section id="consulta" icon={<FileText className="h-5 w-5" />} title="Consulta de Registros Legales">
            <p>
              El módulo de consulta permite a Calidad revisar todos los registros y acceder al detalle completo del
              expediente. La pantalla integra búsqueda, ordenamiento y paginación para facilitar la localización.
            </p>
            <ul className="ml-4 list-disc space-y-1">
              <li>Ingrese a <strong>Consulta de Registros Legales</strong>.</li>
              <li>Utilice filtros o búsquedas para localizar el expediente.</li>
              <li>Revise el estado del registro y la información general.</li>
              <li>Acceda al detalle completo cuando requiera validar datos o documentos.</li>
            </ul>
          </Section>

          <Section id="recomendaciones" icon={<Users className="h-5 w-5" />} title="Recomendaciones operativas">
            <ul className="ml-4 list-disc space-y-1">
              <li>Confirmar desde el inicio el escenario correcto de captura para evitar recapturas.</li>
              <li>Validar con cuidado si el usuario ya existe antes de crear uno nuevo.</li>
              <li>En registros con tramitador, verificar primero que la empresa gestora exista en SICEN.</li>
              <li>Corroborar que la documentación corresponda al tipo de persona capturada.</li>
              <li>Utilizar la consulta para revisar trazabilidad, asociaciones y seguimiento del expediente.</li>
            </ul>
            <p className="border-t pt-4 text-xs text-fg-subtle">
              Esta versión del tutorial está enfocada en el flujo operativo del rol Calidad y resume el comportamiento
              funcional documentado del módulo Registro Legal en SICEN.
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({
  id,
  title,
  icon,
  children,
}: {
  id: string;
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="space-y-3 rounded-lg border bg-surface p-6 shadow-sm">
      <h2 className="flex items-center gap-2 text-base font-semibold uppercase tracking-wide text-fg">
        <span className="text-primary-strong">{icon}</span>
        {title}
      </h2>
      <div className="space-y-3 text-sm leading-relaxed text-fg-muted">{children}</div>
    </section>
  );
}

function FactCard({ label, value, fullWidth }: { label: string; value: string; fullWidth?: boolean }) {
  return (
    <div className={`rounded-lg border bg-surface-2/60 p-3 ${fullWidth ? "sm:col-span-2" : ""}`}>
      <div className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">{label}</div>
      <div className="mt-1 text-sm font-medium text-fg">{value}</div>
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-white">
        {n}
      </span>
      <div>
        <div className="font-semibold text-fg">{title}</div>
        <p className="mt-0.5 text-sm text-fg-muted">{children}</p>
      </div>
    </li>
  );
}

function Variant({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border bg-surface-2/60 p-4">
      <h5 className="text-sm font-semibold text-fg">{title}</h5>
      <p className="mt-1 text-sm leading-relaxed text-fg-muted">{children}</p>
    </div>
  );
}
